import { gql } from '@graphql/__generated__';

export const ORGANISATION_ADDRESS_FRAGMENT = gql(/* GraphQL */ `
  fragment OrganisationAddress on organisations {
    street
    street_number
    postcode
    locality
    state
    country
  }
`);

export const GET_ORGANISATIONS_LIST = gql(/* GraphQL */ `
  query GetOrganisationsList(
    $where: organisations_bool_exp!
    $order_by: [organisations_order_by!]
    $limit: Int!
    $offset: Int!
  ) {
    organisations(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      created_at
      updated_at
      name
      organisation_images(
        order_by: [{ is_primary: desc }, { order_nr: asc }]
        limit: 1
      ) {
        image {
          url
        }
      }
      teams(
        where: { logo_image_id: { _is_null: false } }
        order_by: { created_at: asc }
        limit: 1
      ) {
        id
        logo {
          url
        }
      }
      ...OrganisationAddress
      lat
      lng
      teams_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

export const GET_ORGANISATIONS_LIST_COUNT = gql(/* GraphQL */ `
  query GetOrganisationsListCount($where: organisations_bool_exp!) {
    organisations_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_ORGANISATION = gql(/* GraphQL */ `
  query GetOrganisation($id: uuid!) {
    organisations_by_pk(id: $id) {
      id
      created_at
      updated_at
      name
    }
  }
`);

export const GET_ORGANISATION_MONITORING = gql(/* GraphQL */ `
  query GetOrganisationMonitoring($id: uuid!, $twoYearsAgo: timestamptz!) {
    organisations_by_pk(id: $id) {
      id
      name
      created_at
      organisation_images(
        order_by: [{ is_primary: desc }, { order_nr: asc }]
        limit: 1
      ) {
        image {
          url
        }
      }
      stripe_customer_ids
      close_lead_id
      linked_tenant_id
      sales_rep_user_id
      csm_rep_user_id
      linked_tenant {
        id
        name
      }
      sales_rep {
        id
        full_name
        ...UserAvatar
      }
      csm_rep {
        id
        full_name
        ...UserAvatar
      }
      teams {
        id
        name
        show_in_agency_pages
        agency_slug
        description
        website_url
        active_since
        locality
        country
        logo_image_id
        cover_photo_image_id
        idx_ftp_client_id
        idx_agency_id
        listings_agency_names
        logo {
          id
          url
        }
        cover_photo {
          id
          url
        }
        phone_numbers(
          order_by: [{ primary: desc }, { created_at: desc }]
          limit: 1
        ) {
          number
        }
        teams_users(where: { deactivated: { _eq: false } }) {
          user {
            id
            full_name
            ...UserAvatar
            show_in_agency_pages
            description
            languages
            active_since
            services
            primaryEmail: emails(
              order_by: [{ primary: desc }, { verified: desc }]
              limit: 1
            ) {
              email
            }
            profile_user_images: user_images(
              order_by: [{ is_primary: desc }, { order_nr: asc }]
              limit: 1
            ) {
              image {
                id
                url
              }
            }
            user_transactions_2y: property_transactions_aggregate(
              where: {
                is_published: { _eq: true }
                created_at: { _gte: $twoYearsAgo, _lte: "now()" }
                _not: { lot: { offer_type: { _eq: "rent" } } }
              }
            ) {
              aggregate {
                count
              }
            }
            user_reviews_aggregate: combined_reviews_aggregate(
              where: { type: { _eq: "native" } }
            ) {
              aggregate {
                count
              }
            }
            engagement_reviews: reviews(
              where: { completed: { _eq: true } }
              order_by: { created_at: desc_nulls_last }
              limit: 50
            ) {
              id
              rating
              created_at
            }
          }
        }
        combined_reviews_aggregate(where: { type: { _eq: "native" } }) {
          aggregate {
            count
            avg {
              rating
            }
          }
        }
        combined_reviews(
          where: { type: { _eq: "native" } }
          order_by: { review_date: desc_nulls_last }
          limit: 20
        ) {
          id
          rating
          review_date
        }
        property_transactions_aggregate(
          where: {
            is_published: { _eq: true }
            created_at: { _gte: $twoYearsAgo, _lte: "now()" }
            _not: { lot: { offer_type: { _eq: "rent" } } }
          }
        ) {
          aggregate {
            count
          }
        }
        recent_transactions: property_transactions(
          where: {
            is_published: { _eq: true }
            created_at: { _gte: $twoYearsAgo, _lte: "now()" }
            _not: { lot: { offer_type: { _eq: "rent" } } }
          }
          order_by: { created_at: desc_nulls_last }
          limit: 20
        ) {
          id
          created_at
          signature_date
          purchase_price
        }
      }
    }
  }
`);

export const GET_ORGANISATION_MONITORING_ACTIVITY = gql(/* GraphQL */ `
  query GetOrganisationMonitoringActivity(
    $id: uuid!
    $twoYearsAgo: timestamptz!
    $twoYearsAgoDate: String!
    $sellerContactsAvgPeriodStart: timestamptz!
  ) {
    combined_reviews(
      where: {
        team: { organisation_id: { _eq: $id } }
        review_date: { _gte: $twoYearsAgoDate }
        type: { _eq: "native" }
      }
      order_by: { review_date: asc }
      limit: 10000
    ) {
      id
      review_date
      rating
      team_id
      type
    }
    property_transactions(
      where: {
        seller_broker_team: { organisation_id: { _eq: $id } }
        signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        is_published: { _eq: true }
      }
      order_by: { signature_date: asc }
      limit: 10000
    ) {
      id
      signature_date
      created_at
      seller_broker_team_id
      purchase_price
    }
    seller_contacts_avg_period: lead_agents_aggregate(
      where: {
        source: { _eq: result_page }
        created_at: { _gte: $sellerContactsAvgPeriodStart }
        lead: {
          appraisal_reason: {
            _in: [sell, already_for_sale, inheritance, separation]
          }
        }
        user: {
          teams_users: {
            team: { organisation_id: { _eq: $id } }
            deactivated: { _eq: false }
          }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

/**
 * Window-dependent seller signals for the visibility card. Kept separate from
 * GET_ORGANISATION_MONITORING_ACTIVITY so that switching the card's lookback
 * window only refetches these aggregates — not the whole monitoring dashboard.
 */
export const GET_ORGANISATION_SELLER_SIGNALS = gql(/* GraphQL */ `
  query GetOrganisationSellerSignals($id: uuid!, $windowStart: timestamptz!) {
    seller_contacts_window: lead_agents_aggregate(
      where: {
        source: { _eq: result_page }
        created_at: { _gte: $windowStart }
        lead: {
          appraisal_reason: {
            _in: [sell, already_for_sale, inheritance, separation]
          }
        }
        user: {
          teams_users: {
            team: { organisation_id: { _eq: $id } }
            deactivated: { _eq: false }
          }
        }
      }
    ) {
      aggregate {
        count
      }
    }
    seller_enquiries_window: enquiries_aggregate(
      where: {
        created_at: { _gte: $windowStart }
        team: { organisation_id: { _eq: $id } }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_ORGANISATION_MONITORING_ANALYTICS = gql(/* GraphQL */ `
  query GetOrganisationMonitoringAnalytics(
    $agencyUuids: [uuid!]!
    $startDate: String!
    $endDate: String!
    $hostname: String!
  ) {
    organisation_monitoring_analytics(
      agency_uuids: $agencyUuids
      date_range_start: $startDate
      date_range_end: $endDate
      hostname: $hostname
    ) {
      profile_views_total
      valuation_impressions_total
      phone_clicks_total
      profile_views_by_date {
        date
        count
      }
      valuation_impressions_by_date {
        date
        count
      }
      valuation_impressions_by_position {
        position
        count
      }
    }
  }
`);

export const GET_ORGANISATION_POSTHOG_USAGE = gql(/* GraphQL */ `
  query GetOrganisationPosthogUsage(
    $organisationId: uuid!
    $startDate: String!
    $endDate: String!
  ) {
    organisation_posthog_usage(
      organisation_id: $organisationId
      date_range_start: $startDate
      date_range_end: $endDate
    ) {
      source_usage {
        crm_web {
          active_users_30d
          last_seen_date
        }
        b2b_mobile {
          active_users_30d
          last_seen_date
        }
      }
      cma {
        ...OrganisationPosthogModuleUsage
      }
      visibility {
        ...OrganisationPosthogModuleUsage
      }
      pmap {
        ...OrganisationPosthogModuleUsage
      }
      crm {
        ...OrganisationPosthogModuleUsage
      }
    }
  }

  fragment OrganisationPosthogModuleUsage on OrganisationPosthogModuleUsage {
    active_days_30d
    active_users_30d
    events_30d
    score
    eligible
    source_breakdown {
      crm_web_events
      b2b_mobile_events
    }
    daily_series {
      date
      events
      active_users
      crm_web_events
      b2b_mobile_events
    }
    users {
      user_id
      events_30d
      active_days_30d
      last_seen_date
    }
  }
`);

export const GET_ORGANISATION_ENGAGEMENT_SCORE = gql(/* GraphQL */ `
  query GetOrganisationEngagementScore($organisationId: uuid!) {
    organisation_engagement_score(organisation_id: $organisationId) {
      organisation_id
      score
      previous_score
      updated_at
      signals {
        key
        score
        previous_score
        counted
      }
      history {
        date
        score
        profile
        reviews
        transactions
        visibility
        cma
        prospection_map
        crm
        widget
      }
    }
  }
`);

export const GET_ORGANISATION_VISIBILITY_SCORE = gql(/* GraphQL */ `
  query GetOrganisationVisibilityScore(
    $organisationId: uuid!
    $hostname: String!
    $windowDays: Int
  ) {
    organisation_visibility_score(
      organisation_id: $organisationId
      hostname: $hostname
      window_days: $windowDays
    ) {
      organisation_id
      computed_at
      window_days
      hostname
      country
      delta
      current {
        score
        raw_score
        profile_points
        valuation_points
        profile_views_total
        valuation_views_total
        phone_clicks_total
        profile_views_target
        effective_impressions
        target_impressions
        size_multiplier
        transactions_count_24mo
        seller_contact_target_per_month
        position_breakdown {
          position
          count
          weight
          weighted
        }
        signals {
          id
          points
          max_points
          color
        }
      }
      previous {
        score
        raw_score
        profile_points
        valuation_points
        profile_views_total
        valuation_views_total
        phone_clicks_total
        profile_views_target
        effective_impressions
        target_impressions
        size_multiplier
        transactions_count_24mo
        seller_contact_target_per_month
        position_breakdown {
          position
          count
          weight
          weighted
        }
        signals {
          id
          points
          max_points
          color
        }
      }
    }
  }
`);

export const GET_ORGANISATION_POSTHOG_CSAT = gql(/* GraphQL */ `
  query GetOrganisationPosthogCsat(
    $organisationId: uuid!
    $startDate: String
    $endDate: String
  ) {
    organisation_posthog_csat(
      organisation_id: $organisationId
      date_range_start: $startDate
      date_range_end: $endDate
    ) {
      survey_count
      team_user_count
      response_count
      response_rate_percent
      average_score
      last_response_date
      responses {
        user_id
        score
        responded_at
        survey_id
        survey_name
      }
    }
  }
`);

export const GET_ORGANISATION_BILLING_SUMMARY = gql(/* GraphQL */ `
  query GetOrganisationBillingSummary($organisationId: uuid!) {
    organisation_billing_summary(organisation_id: $organisationId) {
      customer_count
      currency
      subscription_status
      billing_interval
      mrr_amount_cents
      mrr_list_amount_cents
      arr_amount_cents
      next_invoice_date
      next_invoice_amount_cents
      renewal_date
      discount_label
      lifetime_value_amount_cents
      invoices_paid_count
      invoices_outstanding_count
      invoices_outstanding_amount_cents
      history_url
      customer_since
    }
  }
`);

export const UPDATE_ORGANISATION_MONITORING_FIELDS = gql(/* GraphQL */ `
  mutation UpdateOrganisationMonitoringFields(
    $id: uuid!
    $organisation: organisations_set_input!
  ) {
    update_organisations_by_pk(pk_columns: { id: $id }, _set: $organisation) {
      id
      stripe_customer_ids
      close_lead_id
      linked_tenant_id
      sales_rep_user_id
      csm_rep_user_id
      linked_tenant {
        id
        name
      }
      sales_rep {
        id
        full_name
        ...UserAvatar
      }
      csm_rep {
        id
        full_name
        ...UserAvatar
      }
    }
  }
`);

export const SEARCH_STRIPE_CUSTOMERS = gql(/* GraphQL */ `
  query SearchStripeCustomers(
    $query: String!
    $limit: Int = 10
    $organisationId: uuid
  ) {
    search_stripe_customers(
      query: $query
      limit: $limit
      organisation_id: $organisationId
    ) {
      data {
        id
        name
        email
        url
      }
    }
  }
`);

export const SEARCH_CLOSE_LEADS = gql(/* GraphQL */ `
  query SearchCloseLeads(
    $query: String!
    $limit: Int = 10
    $organisationId: uuid
  ) {
    search_close_leads(
      query: $query
      limit: $limit
      organisation_id: $organisationId
    ) {
      data {
        id
        name
        email
        status
        url
      }
    }
  }
`);

export const GET_CLOSE_LEAD = gql(/* GraphQL */ `
  query GetCloseLead($leadId: String!) {
    get_close_lead(lead_id: $leadId) {
      id
      name
      email
      sales_rep_user_id
      csm_rep_user_id
    }
  }
`);

export const DELETE_ORGANISATION = gql(/* GraphQL */ `
  mutation DeleteOrganisation($id: uuid!) {
    delete_organisations_by_pk(id: $id) {
      id
    }
  }
`);

export const GET_ORGANISATION_OVERVIEW = gql(/* GraphQL */ `
  query GetOrganisationOverview($id: uuid!) {
    organisations_by_pk(id: $id) {
      id
      name
      ...OrganisationScoreCards
      teams {
        id
        created_at
        name
        ...TeamAddress_light
        logo {
          id
          url
        }
        teams_users_aggregate(where: { deactivated: { _eq: false } }) {
          aggregate {
            count
          }
        }
      }
    }
  }
`);

export const ADD_TEAM_TO_ORGANISATION = gql(/* GraphQL */ `
  mutation AddTeamToOrganisation($team_id: uuid!, $organisation_id: uuid!) {
    update_teams_by_pk(
      pk_columns: { id: $team_id }
      _set: { organisation_id: $organisation_id }
    ) {
      id
      organisation_id
    }
  }
`);

export const REMOVE_TEAM_FROM_ORGANISATION = gql(/* GraphQL */ `
  mutation RemoveTeamFromOrganisation($team_id: uuid!) {
    update_teams_by_pk(
      pk_columns: { id: $team_id }
      _set: { organisation_id: null }
    ) {
      id
      organisation_id
    }
  }
`);

export const GET_ORGANISATION_IMAGES = gql(/* GraphQL */ `
  query GetOrganisationDetailsImages($organisationId: uuid!) {
    organisation_images(where: { organisation_id: { _eq: $organisationId } }) {
      id
      order_nr
      is_primary
      image {
        id
        url
      }
    }
  }
`);

export const DELETE_ORGANISATION_IMAGE = gql(/* GraphQL */ `
  mutation deleteOrganisationImage($id: uuid!, $imageId: uuid!) {
    delete_organisation_images(where: { id: { _eq: $id } }) {
      returning {
        id
      }
    }
    delete_images_by_pk(id: $imageId) {
      id
    }
  }
`);

export const INSERT_ORGANISATION_IMAGES_BATCH = gql(/* GraphQL */ `
  mutation InsertOrganisationImagesBatch(
    $objects: [organisation_images_insert_input!]!
  ) {
    insert_organisation_images(objects: $objects) {
      returning {
        id
        order_nr
        is_primary
        organisation_id
        image {
          id
          url
        }
      }
    }
  }
`);

export const SET_ORGANISATION_IMAGE_PRIMARY = gql(/* GraphQL */ `
  mutation SetOrganisationImagePrimary($id: uuid!, $organisationId: uuid!) {
    reset_other_images: update_organisation_images(
      where: { organisation_id: { _eq: $organisationId } }
      _set: { is_primary: false }
    ) {
      returning {
        id
        is_primary
        organisation_id
      }
    }

    update_organisation_images(
      where: { id: { _eq: $id } }
      _set: { is_primary: true }
    ) {
      returning {
        id
        is_primary
        organisation_id
      }
    }
  }
`);

export const GET_ORGANISATION_SETTINGS = gql(/* GraphQL */ `
  query GetOrganisationSettings($id: uuid!) {
    organisations_by_pk(id: $id) {
      ...OrganisationSettings
    }
  }
`);

export const GET_ORGANISATION_ACCOUNT_FIELDS = gql(/* GraphQL */ `
  query GetOrganisationAccountFields($id: uuid!) {
    organisations_by_pk(id: $id) {
      id
      name
      stripe_customer_ids
      close_lead_id
      linked_tenant_id
      sales_rep_user_id
      csm_rep_user_id
      emails {
        email
      }
      teams {
        # Deactivated members are intentionally included: a franchise org is
        # often named after its (now deactivated) owner whose personal email is
        # still the billing identity on Stripe/Close — e.g. "Rudy Ponchateau |
        # IAD" whose Stripe customer is registered under ponchateaur@gmail.com.
        teams_users {
          user {
            id
            full_name
            # All emails, not just the primary: an agent's billing account is
            # frequently registered under a personal/secondary address (a gmail)
            # rather than their corporate primary one.
            emails(order_by: [{ primary: desc }, { verified: desc }]) {
              email
            }
          }
        }
      }
    }
  }
`);

export const UPDATE_ORGANISATION_SETTINGS = gql(/* GraphQL */ `
  mutation UpdateOrganisationSettings(
    $id: uuid!
    $organisation: organisations_set_input!
    $phoneNumbers: [phone_numbers_insert_input!]!
    $emails: [emails_insert_input!]!
    $updatePhoneNumbers: Boolean!
    $updateEmails: Boolean!
  ) {
    delete_phone_numbers(where: { organisation_id: { _eq: $id } })
      @include(if: $updatePhoneNumbers) {
      returning {
        id
      }
    }
    insert_phone_numbers(objects: $phoneNumbers)
      @include(if: $updatePhoneNumbers) {
      returning {
        id
      }
    }
    delete_emails(where: { organisation_id: { _eq: $id } })
      @include(if: $updateEmails) {
      returning {
        id
      }
    }
    insert_emails(objects: $emails) @include(if: $updateEmails) {
      returning {
        id
      }
    }
    update_organisations_by_pk(pk_columns: { id: $id }, _set: $organisation) {
      ...OrganisationSettings
    }
  }
`);

export const CREATE_ORGANISATION = gql(/* GraphQL */ `
  mutation CreateOrganisation($organisation: organisations_insert_input!) {
    insert_organisations_one(object: $organisation) {
      id
      created_at
      updated_at
      name
      ...OrganisationAddress
      lat
      lng
    }
  }
`);
